// src/components/policies/AddLifeInsuranceModal.jsx
// Modal for adding/editing life insurance policies

import React, { useState, useEffect } from 'react';
import { X, Heart, Loader2 } from 'lucide-react';
import { createLifeInsurancePolicy, updateLifeInsurancePolicy } from '../../api';

const POLICY_TYPES = [
  { value: 'final_expense', label: 'Final Expense' },
  { value: 'term', label: 'Term Life' },
  { value: 'universal_life', label: 'Universal Life' },
  { value: 'whole_life', label: 'Whole Life' }
];

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'surrendered', label: 'Surrendered' },
  { value: 'paid_up', label: 'Paid Up' }
];

export default function AddLifeInsuranceModal({ isOpen, onClose, clientId, policy, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    carrier: '',
    policyType: 'term',
    policyNumber: '',
    faceAmount: '',
    monthlyPremium: '',
    effectiveDate: '',
    termYears: '',
    beneficiaryName: '',
    beneficiaryRelationship: '',
    status: 'active',
    notes: ''
  });

  const isEditing = !!policy;

  useEffect(() => {
    if (isOpen) {
      if (policy) {
        setForm({
          carrier: policy.carrier || '',
          policyType: policy.policyType || 'term',
          policyNumber: policy.policyNumber || '',
          faceAmount: policy.faceAmount || '',
          monthlyPremium: policy.monthlyPremium || '',
          effectiveDate: policy.effectiveDate?.split('T')[0] || '',
          termYears: policy.termYears || '',
          beneficiaryName: policy.beneficiaryName || '',
          beneficiaryRelationship: policy.beneficiaryRelationship || '',
          status: policy.status || 'active',
          notes: policy.notes || ''
        });
      } else {
        setForm({
          carrier: '',
          policyType: 'term',
          policyNumber: '',
          faceAmount: '',
          monthlyPremium: '',
          effectiveDate: '',
          termYears: '',
          beneficiaryName: '',
          beneficiaryRelationship: '',
          status: 'active',
          notes: ''
        });
      }
    }
  }, [isOpen, policy]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.carrier || !form.policyType) {
      alert('Please fill in required fields');
      return;
    }

    try {
      setSaving(true);

      const data = {
        ...form,
        faceAmount: form.faceAmount ? parseFloat(form.faceAmount) : null,
        monthlyPremium: form.monthlyPremium ? parseFloat(form.monthlyPremium) : null,
        termYears: form.termYears ? parseInt(form.termYears) : null,
        effectiveDate: form.effectiveDate || null
      };

      if (isEditing) {
        await updateLifeInsurancePolicy(clientId, policy.id, data);
      } else {
        await createLifeInsurancePolicy(clientId, data);
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('[AddLifeInsuranceModal] Save error:', err);
      alert('Failed to save policy: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
              <Heart className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">
                {isEditing ? 'Edit' : 'Add'} Life Insurance
              </h2>
              <p className="text-sm text-slate-500">
                {isEditing ? 'Update policy details' : 'Enter policy information'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Carrier & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Carrier *
              </label>
              <input
                type="text"
                value={form.carrier}
                onChange={(e) => handleChange('carrier', e.target.value)}
                placeholder="e.g., Mutual of Omaha"
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Policy Type *
              </label>
              <select
                value={form.policyType}
                onChange={(e) => handleChange('policyType', e.target.value)}
                required
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {POLICY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Policy Number */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Policy Number
            </label>
            <input
              type="text"
              value={form.policyNumber}
              onChange={(e) => handleChange('policyNumber', e.target.value)}
              placeholder="e.g., LI-2024-12345"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Face Amount & Premium */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Face Amount
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input
                  type="number"
                  value={form.faceAmount}
                  onChange={(e) => handleChange('faceAmount', e.target.value)}
                  placeholder="50,000"
                  className="w-full pl-8 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Monthly Premium
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input
                  type="number"
                  value={form.monthlyPremium}
                  onChange={(e) => handleChange('monthlyPremium', e.target.value)}
                  placeholder="89"
                  className="w-full pl-8 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Effective Date & Term */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Effective Date
              </label>
              <input
                type="date"
                value={form.effectiveDate}
                onChange={(e) => handleChange('effectiveDate', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            {form.policyType === 'term' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Term (Years)
                </label>
                <input
                  type="number"
                  value={form.termYears}
                  onChange={(e) => handleChange('termYears', e.target.value)}
                  placeholder="20"
                  className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            )}
          </div>

          {/* Beneficiary */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Beneficiary Name
              </label>
              <input
                type="text"
                value={form.beneficiaryName}
                onChange={(e) => handleChange('beneficiaryName', e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Relationship
              </label>
              <input
                type="text"
                value={form.beneficiaryRelationship}
                onChange={(e) => handleChange('beneficiaryRelationship', e.target.value)}
                placeholder="Spouse"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
          </div>

          {/* Status */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={3}
              placeholder="Additional notes..."
              className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-xl disabled:opacity-50 transition-colors"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEditing ? 'Save Changes' : 'Add Policy'}
          </button>
        </div>
      </div>
    </div>
  );
}
